import { ChangeDetectorRef, Component, HostBinding, Inject, OnInit, Optional } from '@angular/core';
import { TlkModalWindowRef, TLK_MODAL_WINDOW_DATA } from '@itero/ui-toolkit-angular/modal-window';
import { IuiModalWindowRef, IUI_MODAL_WINDOW_DATA } from '@itero/ui-components-angular/modal-window';
import { UserSettings } from '../settings.service';
import { GlobalSettingsService } from '../../core/globalSettings.service';
import { KeyValuePair } from '../../shared/generalInterfaces';
import { CaseTypeEnum, FeatureToggle, SoftwareOptionsForCompany } from '../../shared/enums';
import { Subscription, tap } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BaseDestroyable } from '../../core/base-destroyable';
import { DownloadFileInfo, ExportInfoDto, CreateOrthodonticsExportFileRequestDto } from '../downloadNotification/fileInfo.model';
import { DownloadNotificationService } from '../downloadNotification/downloadNotification.service';
import { ISettingsData } from '../../interfaces/ISettingsData';
import { SoftwareOptionsService } from 'app/core/companySoftwareOptions/service/softwareOptions.service';
import { TranslateService } from '@ngx-translate/core';
import { BiAction, BiObjectType } from '@logging/interfaces/bi.enums';
import { FeaturesToggleSettingsService } from 'app/featuresToggleSettings/service/featuresToggleSettings.service';
import { Consts } from '../consts';
import { TimberService } from '@logging/timber.service';
import { FaceliftQuery } from '@shared/facelift-store/facelift.query';

export interface BgColorOption {
	key: string;
	label: string;
	color: string;
}

export const BACKGROUND_COLOR_MAP: Record<string, string> = {
	White: '#ffffff',
	Grey:  '#808080',
	Dark:  '#404040',
	Black: '#000000',
};

export interface OrthoCadExportModalData {
	caseTypeId: number;
	orderId: number;
	orderDetailsId: number;
	settings: ISettingsData;
}

@Component({
	selector: 'eup-orthoCadExport',
	templateUrl: './orthoCadExport.component.html',
	styleUrls: [ './orthoCadExport.component.scss' ],
	host: {
		'id': 'modal-ortho-export'
	}
})
export class OrthoCadExportModal extends BaseDestroyable implements OnInit {
	caseTypeId: number;
	orderId: number;
	orderDetailsId: number;
	exportFileRequest: CreateOrthodonticsExportFileRequestDto = new CreateOrthodonticsExportFileRequestDto();
	exportTypeOptions: KeyValuePair[] = null;
	dataFormatOptions: KeyValuePair[] = null;
	fileTypeOptions: KeyValuePair[] = null;
	hidePatientName: boolean;
	selectedExportTypeOption: KeyValuePair | null = null;
	selectedDataFormatOption: KeyValuePair | null = null;
	selectedFileTypeOption: KeyValuePair | null = null;
	exportSubscription: Subscription;
  	exportWoSubscription: Subscription;
	defaultFileType: string;
	disablePatientDetailsBySWOpiont: boolean;
	imageBgInExportModal = false;
	imageBgOptions: BgColorOption[] = [];
	selectedBgOption: BgColorOption | null = null;
	bgTranslationMap: Record<string, string> = {};

	isFacelift = false;

	@HostBinding('class.facelift') get faceliftClass(): boolean { return this.isFacelift; }

	getOptionName = (option: KeyValuePair): string => option.value;
	compareWith = (v1: KeyValuePair, v2: KeyValuePair): boolean => v1?.key === v2?.key;

	getBgOptionName = (option: BgColorOption): string => option.label;
	compareBgWith = (v1: BgColorOption, v2: BgColorOption): boolean => v1?.key === v2?.key;
	getBgOptionStyle = (_index: number, option: BgColorOption): { [p: string]: any } => ({
		'background-image': `linear-gradient(${option.color}, ${option.color}), linear-gradient(#3E3D40, #3E3D40)`,
		'background-size': '17px 17px, 19px 19px', // Inner color 17px, outer border 19px (to account for 1px border)
		'background-repeat': 'no-repeat, no-repeat',
		'background-position': '17px center, 16px center',
		'padding-left': '51px',
	});

	data: OrthoCadExportModalData;
	private readonly tlkModalWindowRef: TlkModalWindowRef<void> | null;
	private readonly iuiModalWindowRef: IuiModalWindowRef<void> | null;

	constructor(
		private globalSettings: GlobalSettingsService,
		private downloadNotificationService: DownloadNotificationService,
		private softwareOptionsService: SoftwareOptionsService,
		private featuresToggleSettingsService: FeaturesToggleSettingsService,
		private translateService: TranslateService,
		private timberService: TimberService,
		private cdr: ChangeDetectorRef,
		private faceliftQuery: FaceliftQuery,
		@Optional() tlkRef: TlkModalWindowRef<void>,
		@Optional() @Inject(TLK_MODAL_WINDOW_DATA) tlkData: OrthoCadExportModalData | null,
		@Optional() iuiRef: IuiModalWindowRef<void>,
		@Optional() @Inject(IUI_MODAL_WINDOW_DATA) iuiData: OrthoCadExportModalData | null,
	) {
		super();
		this.tlkModalWindowRef = tlkRef;
		this.iuiModalWindowRef = iuiRef;
		this.data = (iuiData ?? tlkData)!;
	}

	ngOnInit(): void {
		this.isFacelift = this.faceliftQuery.getValue().isFaceliftEnabled;
		this.caseTypeId = this.data.caseTypeId;
		this.orderId = this.data.orderId;
		this.orderDetailsId = this.data.orderDetailsId;

		this.translateService.get('ExportModalBackgroundOptions')
			.pipe(
				tap((translations: Record<string, string>) => {
					this.bgTranslationMap = translations;
					this.imageBgOptions = Object.entries(translations).map(([key, label]) => ({
						key,
						label,
						color: BACKGROUND_COLOR_MAP[key] ?? '#ffffff',
					}));
					const savedKey = localStorage.getItem(Consts.Storage.ExportModalImageBg);
					this.selectedBgOption =
						(savedKey && this.imageBgOptions.find((o) => o.key === savedKey)) ||
						this.imageBgOptions.find((o) => o.key === 'White') ||
						this.imageBgOptions[0] ||
						null;
				}),
				takeUntil(this.componentAlive$)
			)
			.subscribe();

		this.featuresToggleSettingsService.getFeaturesToggleSettings().pipe(tap(() => {
			this.imageBgInExportModal = this.featuresToggleSettingsService.isVisibleFeature(FeatureToggle.ImageBgInExportModal, false);
		}), takeUntil(this.componentAlive$)).subscribe();

		this.disablePatientDetailsBySWOpiont = this.softwareOptionsService.isSoftwareOptionExists(
			SoftwareOptionsForCompany.shouldDisableHidePatientDetails,
			this.globalSettings.get().companySoftwareOptions
		);

		this.applySettings(this.data.settings);
	}



	private applySettings(settingsRes: ISettingsData): void {
		this.setExportTypeOptionsByCaseType(settingsRes);
		this.dataFormatOptions = settingsRes.dataFormatOptions.filter((o) => +o.key > 0);
		this.fileTypeOptions = settingsRes.fileTypeOptions.filter((o) => +o.key > 0);
		this.hidePatientName = settingsRes.userSettings.patientAnonymization;
		this.cdr.detectChanges();

		// tlk-select's ngOnChanges resets its internal value via correspondedValueOrNull whenever [options] changes.
		// That runs synchronously during this change-detection cycle, so any [ngModel] we set here would be
		// overwritten. Deferring to the next microtask lets the current CD cycle finish (and ngOnChanges run)
		// before we apply the user's saved selection. There is no explicit "options settled" event on tlk-select.
		queueMicrotask(() => {
			this.setExportOptions(settingsRes.userSettings);
			this.cdr.detectChanges();
		});
	}

	private setExportTypeOptionsByCaseType(settings: ISettingsData): void {
		switch (this.caseTypeId) {
			case CaseTypeEnum.iCast: {
				this.exportTypeOptions = settings.exportTypeICastOptions.filter((o) => +o.key > 0);
				break;
			}
			default: {
				this.exportTypeOptions = settings.exportTypeIRecordOptions.filter((o) => +o.key > 0);
				break;
			}
		}
	}

	private setExportOptions(userSettings: UserSettings): void {
		switch (this.caseTypeId) {
			case CaseTypeEnum.iCast: {
				this.selectedExportTypeOption =
					userSettings.exportTypeForiCast > 0
						? this.exportTypeOptions.find((o) => o.key.toString() === userSettings.exportTypeForiCast.toString()) ?? null
						: null;
				break;
			}
			case CaseTypeEnum.iRecord:
			case CaseTypeEnum.InvisalignAndiRecord: {
				this.selectedExportTypeOption =
					userSettings.exportTypeForiRecord > 0
						? this.exportTypeOptions.find((o) => o.key.toString() === userSettings.exportTypeForiRecord.toString()) ?? null
						: null;
				break;
			}
			default: {
				this.selectedExportTypeOption = null;
				break;
			}
		}

		this.selectedDataFormatOption =
			userSettings.dataFormat > 0
				? this.dataFormatOptions.find((o) => o.key.toString() === userSettings.dataFormat.toString()) ?? null
				: null;

		this.defaultFileType = this.fileTypeOptions.find((o) => o.key.toString() === '1')?.value ?? '';
		this.selectedFileTypeOption =
			userSettings.fileType > 0
				? this.fileTypeOptions.find((o) => o.key.toString() === userSettings.fileType.toString()) ?? null
				: this.fileTypeOptions.find((o) => o.key.toString() === '1') ?? null;
	}

  export(): void {
	if (this.canExport()) {
		this.exportFileRequest.fileFormat = +this.selectedDataFormatOption!.key;
		this.exportFileRequest.fileType = +this.selectedFileTypeOption!.key;
		this.exportFileRequest.orderId = this.orderId;
		this.exportFileRequest.orderDetailsId = this.orderDetailsId;
		this.exportFileRequest.typeFormat = +this.selectedExportTypeOption!.key;

		const settings = this.globalSettings.get();
		var requestData = this.createRequestData(settings.contactId);

		this.exportWoSubscription = this.downloadNotificationService
			.getOrthodonticsExportInfo(requestData)
			.subscribe((res: ExportInfoDto) => {
				if (res && res.isFileExists) {
					const fileInfo: DownloadFileInfo = {
					orderId: requestData.orderId,
					orderDetailsId: requestData.orderDetailsId,
					downloaded: true,
					requestData: requestData
					};
					this.downloadNotificationService.downloadFile(fileInfo);
				} else {
					this.downloadNotificationService.addFile(requestData);
				}
			});

	this.closeModal();
    }
  }

  close(): void {
	this.closeModal();
  }

  private closeModal(): void {
	(this.iuiModalWindowRef ?? this.tlkModalWindowRef)!.close();
  }

  createRequestData(clientId: number): CreateOrthodonticsExportFileRequestDto {
	var requestData = new CreateOrthodonticsExportFileRequestDto();
	requestData.clientId = clientId;
	requestData.fileFormat = this.exportFileRequest.fileFormat;
	requestData.fileType = this.exportFileRequest.fileType;
	requestData.typeFormat = this.exportFileRequest.typeFormat;
	requestData.backgroundColor = (this.selectedBgOption?.key.toLowerCase() ?? 'white') as 'white' | 'grey' | 'dark' | 'black';
	requestData.orderId = this.exportFileRequest.orderId;
	requestData.orderDetailsId = this.exportFileRequest.orderDetailsId;
	requestData.patientAnonymization = this.disablePatientDetailsBySWOpiont ? false : this.hidePatientName;
	var today = new Date();
	requestData.creationTime = today.toUTCString();

	return requestData;
}

  onExportTypeChange(option: KeyValuePair): void {
		this.selectedExportTypeOption = option ?? null;
		this.logDropdownChange('ExportTypeDropdown', option?.value);
	}

	onDataFormatChange(option: KeyValuePair): void {
		this.selectedDataFormatOption = option ?? null;
		this.logDropdownChange('DataFormatDropdown', option?.value);
	}

	onFileTypeChange(option: KeyValuePair): void {
		this.selectedFileTypeOption = option ?? null;
		this.logDropdownChange('FileTypeDropdown', option?.value);
	}

	onBgChange(option: BgColorOption): void {
		this.selectedBgOption = option ?? null;
		localStorage.setItem(Consts.Storage.ExportModalImageBg, option.key);
		this.logDropdownChange('ImageBackgroundDropdown', option?.key);
	}

	private logDropdownChange(objectName: string, selectedValue: string | undefined): void {
		this.timberService.userActionEvent({
			locationParentObject: 'OrthoCadExportModal',
			objectName,
			objectType: BiObjectType.Select,
			action: selectedValue ? BiAction.Select : BiAction.Deselect,
			selectedValue,
		});
	}

	canExport(): boolean {
		return (
			this.selectedDataFormatOption != null && +this.selectedDataFormatOption.key > 0 &&
			this.selectedExportTypeOption != null && +this.selectedExportTypeOption.key > 0 &&
			this.selectedFileTypeOption != null && +this.selectedFileTypeOption.key > 0
		);
	}

	onHidePatientNameChanged(newVal: boolean): void {
		this.hidePatientName = newVal;
	}
}
